import { axiosInstance } from '@/http-common'

/**
 * Get assets with latest parameters for DevExtreme DataGrid
 * Supports pagination and search filtering
 * @param skip - Number of records to skip (for pagination)
 * @param take - Number of records to take (page size)
 * @param searchValue - Search term for filtering by name or serial_number
 */
export async function getAssetsGrid(skip: number = 0, take: number = 25, searchValue: string = ''): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get<Array<any>>(`/api/v1/assets/grid`, {
      params: {
        skip,
        take,
        searchValue
      }
    })
      .then(response => {
        resolve(response)
      })
      .catch(response => {
        reject(response)
      })
  })
}

/**
 * Get assets with their latest parameters
 * Returns all assets for the current company
 */
export async function getAssetsWithLatestParams(): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get('/api/v1/assets/with-latest-params')
      .then(response => {
        resolve(response)
      })
      .catch(response => {
        reject(response)
      })
  })
}

/**
 * Get single asset details with latest parameters
 * Used in AssetDetailsView to display full asset information
 * @param id - Asset ID
 */
export async function getAssetDetails(id: number | string, source?: 'asset' | 'device'): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/assets/${id}/details`, {
      params: source ? { source } : undefined
    })
      .then(response => {
        resolve(response)
      })
      .catch(response => {
        reject(response)
      })
  })
}

/**
 * Get single asset by ID
 * Returns basic asset information without latest parameters
 * @param id - Asset ID
 */
export async function getAsset(id: number | string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/asset/${id}`)
      .then(response => {
        resolve(response)
      })
      .catch(response => {
        reject(response)
      })
  })
}

/**
 * Get historical parameters for an asset within selected date range
 * Returns LineChart-ready structure
 * @param id - Asset ID
 * @param dateFrom - ISO string date from
 * @param dateTo - ISO string date to
 */
export async function getAssetHistoryRange(
  id: number | string,
  dateFrom: string,
  dateTo: string,
  source?: 'asset' | 'device'
): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/assets/${id}/history`, {
      params: source ? {
        date_from: dateFrom,
        date_to: dateTo,
        source
      } : {
        date_from: dateFrom,
        date_to: dateTo
      }
    })
      .then(response => resolve(response))
      .catch(response => reject(response))
  })
}

/**
 * Get energy statistics (totals + period deltas) for a WAGO asset
 */
export async function getAssetEnergyStats(id: number | string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/assets/${id}/energy-stats`)
      .then(response => resolve(response))
      .catch(response => reject(response))
  })
}

/**
 * Get DKN LMS alarm occurrences (value=1) since midnight of the current day
 */
export async function getAssetAlarms(id: number | string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/assets/${id}/alarms`)
      .then(response => resolve(response))
      .catch(response => reject(response))
  })
}

/**
 * Check if serial number already exists
 * Used for validation when creating or editing assets
 * @param serialNumber - Serial number to check
 * @param excludeId - Asset ID to exclude from check (for edit mode)
 */
export async function checkAssetSerialNumber(serialNumber: string, excludeId?: number | string): Promise<any> {
  return new Promise((resolve, reject) => {
    const params: any = { serialNumber }
    if (excludeId) {
      params.excludeId = excludeId
    }

    axiosInstance.get('/api/v1/asset/check-serial', { params })
      .then(response => {
        resolve(response)
      })
      .catch(response => {
        reject(response)
      })
  })
}
